import { createAsyncThunk } from '@reduxjs/toolkit'
import { createThunkName } from '../../../utils/createThunkname'
import {
  CalculatePriceQueryParams,
  CancelTransactionPerCustomerDto,
  CreateTransactionDto,
  ExportTransactionsQueryParams,
  GetAllTransactionsQueryParams,
  GetAllTransactionsResponse,
  GetCalculatePriceResponse,
  GetCustomerDetailResponse,
  GetCustomerListFromExcelResponse,
  GetCustomersQueryParams,
  GetCustomersResponse,
  GetInsurancePlanResponse,
  GetPriceResponse,
  GetTravelDetailResponse,
  SearchCustomerExcelDto,
  SearchCustomerExcelQueryParams,
  GetSearchCustomerListFromExcelResponse,
  UpdateTransactionPerCustomerDto,
  GetInsurersResponse,
  GetPlansQueryParams,
  GetPlansResponse,
  GetTransactionTimestampResponse,
  GetTransactionStatusResponse,
  UpdateTransactionStatusDto,
  GetCustomerListFromExcelRequest,
  DownloadCustomerTemplateQueryParams,
  GetIntegrationMessageResponse
} from './types'
import { AxiosService } from '../../../services/api'
import { TRANSACTION_URLS } from '../../../configs/api'
import { REQUEST_OPTIONS } from '../../../constants/request-options'
import { saveData } from '../../../utils/downloadFile'

const PREFIX = 'transaction'

export const TransactionService = {
  getAllTransactionsAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getAllTransactions'),
    async (queryParams: GetAllTransactionsQueryParams) => {
      try {
        const result = await AxiosService.get<GetAllTransactionsResponse>(TRANSACTION_URLS.getAllTransactions, {
          params: queryParams
        })
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  exportTransactionsAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'exportTransactions'),
    async (queryParams: ExportTransactionsQueryParams) => {
      try {
        const result = await AxiosService.get(TRANSACTION_URLS.exportTransactions, {
          params: queryParams,
          ...REQUEST_OPTIONS
        })
        saveData(result, result.request.getResponseHeader('Content-Disposition'))
      } catch (error) {
        throw error
      }
    }
  ),
  createTransactionAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'createTransaction'),
    async (dto: CreateTransactionDto) => {
      try {
        await AxiosService.post(TRANSACTION_URLS.createTransaction, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updateTransactionPerCustomerAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateTransactionPerCustomer'),
    async (dto: UpdateTransactionPerCustomerDto) => {
      try {
        await AxiosService.put(TRANSACTION_URLS.updateTransactionPerCustomer, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  cancelTransactionPerCustomerAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'cancelTransactionPerCustomer'),
    async (dto: CancelTransactionPerCustomerDto) => {
      try {
        await AxiosService.put(TRANSACTION_URLS.cancelTransactionPerCustomer, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  calculatePriceAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'calculatePrice'),
    async (queryParams: CalculatePriceQueryParams) => {
      try {
        const result = await AxiosService.get<GetCalculatePriceResponse>(TRANSACTION_URLS.calculatePrice, {
          params: queryParams
        })
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  downloadCustomerTemplateAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'downloadCustomerTemplate'),
    async (queryParams: DownloadCustomerTemplateQueryParams) => {
      try {
        const result = await AxiosService.get(TRANSACTION_URLS.downloadCustomerTemplate, {
          params: queryParams,
          ...REQUEST_OPTIONS
        })
        saveData(result, result.request.getResponseHeader('Content-Disposition'))
      } catch (error) {
        throw error
      }
    }
  ),
  getCustomerListFromExcelAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCustomerListFromExcel'),
    async (request: GetCustomerListFromExcelRequest) => {
      try {
        const formData = new FormData()
        formData.append('file', request.file)
        const result = await AxiosService.post<GetCustomerListFromExcelResponse>(
          TRANSACTION_URLS.getCustomerListFromExcel,
          formData,
          {
            params: request.queryParams
          }
        )
        return result.data.customers
      } catch (error) {
        throw error
      }
    }
  ),
  exportTransactionCustomersAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'exportTransactionCustomers'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get(TRANSACTION_URLS.exportTransactionCustomers(transactionId), {
          ...REQUEST_OPTIONS
        })
        saveData(result, result.request.getResponseHeader('Content-Disposition'))
      } catch (error) {
        throw error
      }
    }
  ),
  getTravelDetailAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getTravelDetail'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get<GetTravelDetailResponse>(TRANSACTION_URLS.getTravelDetail(transactionId))
        return result.data.transaction
      } catch (error) {
        throw error
      }
    }
  ),
  getInsurancePlanAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getInsurancePlan'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get<GetInsurancePlanResponse>(
          TRANSACTION_URLS.getInsurancePlan(transactionId)
        )
        return result.data.transaction
      } catch (error) {
        throw error
      }
    }
  ),
  getCustomersAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCustomers'),
    async ({ transactionId, queryParams }: { transactionId: string; queryParams: GetCustomersQueryParams }) => {
      try {
        const result = await AxiosService.get<GetCustomersResponse>(TRANSACTION_URLS.getCustomers(transactionId), {
          params: queryParams
        })
        return result.data.transaction
      } catch (error) {
        throw error
      }
    }
  ),
  getPriceAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getPrice'), async (transactionId: string) => {
    try {
      const result = await AxiosService.get<GetPriceResponse>(TRANSACTION_URLS.getPrice(transactionId))
      return result.data.transaction
    } catch (error) {
      throw error
    }
  }),
  getCustomerDetailAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getCustomerDetail'),
    async ({ transactionId, customerId }: { transactionId: string; customerId: string }) => {
      try {
        const result = await AxiosService.get<GetCustomerDetailResponse>(
          TRANSACTION_URLS.getCustomerDetail(transactionId, customerId)
        )
        return result.data.transaction
      } catch (error) {
        throw error
      }
    }
  ),
  searchCustomerExcelAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'searchCustomerExcel'),
    async ({ queryParams, dto }: { queryParams: SearchCustomerExcelQueryParams; dto: SearchCustomerExcelDto }) => {
      try {
        const result = await AxiosService.post<GetSearchCustomerListFromExcelResponse>(
          TRANSACTION_URLS.searchCustomerExcel,
          dto,
          {
            params: queryParams
          }
        )
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  getInsurersAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getInsurers'), async () => {
    try {
      const result = await AxiosService.get<GetInsurersResponse>(TRANSACTION_URLS.getInsurers)
      return result.data.insurers
    } catch (error) {
      throw error
    }
  }),
  getPlansAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getPlans'),
    async (queryParams: GetPlansQueryParams) => {
      try {
        const result = await AxiosService.get<GetPlansResponse>(TRANSACTION_URLS.getPlans, { params: queryParams })
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  getTransactionTimestampAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getTransactionTimestamp'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get<GetTransactionTimestampResponse>(
          TRANSACTION_URLS.getTransactionTimestamp(transactionId)
        )
        return result.data.transaction
      } catch (error) {
        throw error
      }
    }
  ),
  getTransactionStatusAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getTransactionStatus'),
    async (transactionId: string) => {
      try {
        const result = await AxiosService.get<GetTransactionStatusResponse>(
          TRANSACTION_URLS.getTransactionStatus(transactionId)
        )
        return result.data.isActive
      } catch (error) {
        throw error
      }
    }
  ),
  updateTransactionStatusAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateTransactionStatus'),
    async (dto: UpdateTransactionStatusDto) => {
      try {
        await AxiosService.put(TRANSACTION_URLS.updateTransactionStatus, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  getIntegrationMessage: async (transactionId: string) => {
    try {
      const result = await AxiosService.get<GetIntegrationMessageResponse>(
        TRANSACTION_URLS.getIntegrationMessage(transactionId)
      )
      return result.data
    } catch (error) {
      throw error
    }
  }
}
